import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/i18n/LanguageContext";
import AnalyticsListener from "./components/AnalyticsListener";
import SiteJsonLd from "./components/SiteJsonLd";
import Index from "./pages/Index";

// Code-split: every non-home route is loaded on demand to keep
// the initial bundle small (LCP/TTI win on the homepage).
const Services = lazy(() => import("./pages/Services"));
const ProductionAISystems = lazy(() => import("./pages/ProductionAISystems"));
const AgenticAIWorkflows = lazy(() => import("./pages/AgenticAIWorkflows"));
const DataPlatforms = lazy(() => import("./pages/DataPlatforms"));
const DecisionIntelligence = lazy(() => import("./pages/DecisionIntelligence"));
const AIReadinessAudit = lazy(() => import("./pages/AIReadinessAudit"));
const AIReadinessScorecard = lazy(() => import("./pages/AIReadinessScorecard"));
const Solutions = lazy(() => import("./pages/Solutions"));
const AIAgentsWorkflowAutomation = lazy(() => import("./pages/solutions/AIAgentsWorkflowAutomation"));
const DocumentIntelligence = lazy(() => import("./pages/solutions/DocumentIntelligence"));
const KnowledgeSystems = lazy(() => import("./pages/solutions/KnowledgeSystems"));
const CustomerSupportSystems = lazy(() => import("./pages/solutions/CustomerSupportSystems"));
const PredictiveModelsDecisionScience = lazy(() => import("./pages/solutions/PredictiveModelsDecisionScience"));
const DataPlatformsForAI = lazy(() => import("./pages/solutions/DataPlatformsForAI"));
const Industries = lazy(() => import("./pages/Industries"));
const AccountingFirms = lazy(() => import("./pages/industries/AccountingFirms"));
const ManufacturingDistribution = lazy(() => import("./pages/industries/ManufacturingDistribution"));
const RetailEcommerceAI = lazy(() => import("./pages/industries/RetailEcommerceAI"));
const CpgAI = lazy(() => import("./pages/industries/CpgAI"));
const FranchiseMultiLocationAI = lazy(() => import("./pages/industries/FranchiseMultiLocationAI"));
const ProfessionalServicesAI = lazy(() => import("./pages/industries/ProfessionalServicesAI"));
const B2BServicesAI = lazy(() => import("./pages/industries/B2BServicesAI"));
const Work = lazy(() => import("./pages/Work"));
const VetAIVendor = lazy(() => import("./pages/authority/VetAIVendor"));
const ProductionGradeAI = lazy(() => import("./pages/authority/ProductionGradeAI"));
const WorkflowsDeserveAI = lazy(() => import("./pages/authority/WorkflowsDeserveAI"));
const About = lazy(() => import("./pages/About"));
const Insights = lazy(() => import("./pages/Insights"));
const Contact = lazy(() => import("./pages/Contact"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));

const routerFuture = {
  v7_relativeSplatPath: true,
  v7_startTransition: true,
} as const;

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <LanguageProvider>
      <BrowserRouter future={routerFuture}>
        <AnalyticsListener />
        <SiteJsonLd />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/production-ai-systems" element={<ProductionAISystems />} />
            <Route path="/services/agentic-ai-workflows" element={<AgenticAIWorkflows />} />
            <Route path="/services/data-platforms" element={<DataPlatforms />} />
            <Route path="/services/decision-intelligence" element={<DecisionIntelligence />} />
            <Route path="/services/ai-readiness-audit" element={<AIReadinessAudit />} />
            <Route path="/ai-readiness-scorecard" element={<AIReadinessScorecard />} />
            <Route path="/services/agentic-ai" element={<Navigate to="/services/agentic-ai-workflows" replace />} />
            <Route path="/services/generative-ai" element={<Navigate to="/services/production-ai-systems" replace />} />
            <Route path="/services/predictive-ai" element={<Navigate to="/services/decision-intelligence" replace />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/solutions/ai-agents-workflow-automation" element={<AIAgentsWorkflowAutomation />} />
            <Route path="/solutions/document-intelligence" element={<DocumentIntelligence />} />
            <Route path="/solutions/knowledge-systems-enterprise-retrieval" element={<KnowledgeSystems />} />
            <Route path="/solutions/customer-support-service-systems" element={<CustomerSupportSystems />} />
            <Route path="/solutions/predictive-models-decision-science" element={<PredictiveModelsDecisionScience />} />
            <Route path="/solutions/data-platforms-for-ai" element={<DataPlatformsForAI />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/industries/retail-ecommerce-ai" element={<RetailEcommerceAI />} />
            <Route path="/industries/cpg-ai" element={<CpgAI />} />
            <Route path="/industries/franchise-multi-location-ai" element={<FranchiseMultiLocationAI />} />
            <Route path="/industries/professional-services-ai" element={<ProfessionalServicesAI />} />
            <Route path="/industries/b2b-services-ai" element={<B2BServicesAI />} />
            <Route path="/industries/accounting-firms" element={<AccountingFirms />} />
            <Route path="/industries/manufacturing-distribution" element={<ManufacturingDistribution />} />
            <Route path="/industries/service-businesses" element={<Navigate to="/industries/professional-services-ai" replace />} />
            <Route path="/industries/ecommerce-operations" element={<Navigate to="/industries/retail-ecommerce-ai" replace />} />
            <Route path="/work" element={<Work />} />
            <Route path="/insights/vet-ai-vendor" element={<VetAIVendor />} />
            <Route path="/insights/production-grade-ai" element={<ProductionGradeAI />} />
            <Route path="/insights/workflows-deserve-ai" element={<WorkflowsDeserveAI />} />
            <Route path="/about" element={<About />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<ArticleDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
